import { Accessor, createEffect, createSignal, onCleanup } from 'solid-js';

/**
 * Tracks the live pixel width of an element via {@link ResizeObserver}.
 *
 * Pass an accessor for the observed element so the observer re-attaches
 * when the ref changes (or is set after mount). The width updates on every
 * resize and is cleaned up automatically when the owning scope disposes.
 *
 * @param element accessor for the element to observe
 * @param fallback width reported before the element is available / measured
 * (defaults to {@link Infinity} so width-tiered layouts start at their widest)
 */
export const createElementWidth = (
  element: Accessor<HTMLElement | undefined>,
  fallback = Infinity
): Accessor<number> => {
  const [width, setWidth] = createSignal(fallback);

  createEffect(() => {
    const el = element();
    if (!el) return;

    const update = (): void => {
      setWidth(el.getBoundingClientRect().width);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  });

  return width;
};
